/**
 * Einwilligung für nicht notwendige Cookies (Google Consent Mode v2).
 *
 * Zwei Ebenen, die bewusst unabhängig voneinander funktionieren:
 *
 * 1. **Rechtliche Ebene — Google löst die Region aus der IP auf.**
 *    `public/static/scripts/gtag/gtag-stub.js` setzt vor dem Laden von gtag.js
 *    `gtag('consent','default', …)` mit `region: [EWR + UK]` auf „denied“ und
 *    global auf „granted“. Ohne Einwilligung speichert Google in diesen Ländern
 *    also nichts auf dem Gerät — unabhängig davon, ob unser Banner erscheint.
 *
 * 2. **UI-Ebene — Zeitzone entscheidet, wer das Banner sieht.**
 *    Im Browser gibt es keine verlässliche Länderkennung ohne Geo-IP-Dienst;
 *    einen solchen Dritten für ein Datenschutz-Feature einzubinden wäre absurd.
 *    Die Zeitzone kostet keine Anfrage und liegt lokal vor.
 *
 * Der Fehlerfall ist damit ungefährlich: Erkennt die Heuristik jemanden in der
 * EU nicht, bleibt es bei „denied“ aus Ebene 1 — es werden keine Cookies
 * gesetzt, die Person kann nur nicht aktiv zustimmen. Umgekehrt kann niemand
 * durch eine falsch geratene Zeitzone getrackt werden.
 *
 * Die Schweiz ist bewusst NICHT dabei: Das DSG verlangt für Reichweitenmessung
 * keine Einwilligung, sondern Transparenz und ein Widerspruchsrecht (beides in
 * Abschnitt 5 der Datenschutzerklärung).
 */

/**
 * Speicherschlüssel der Entscheidung.
 *
 * ACHTUNG: identisch in `public/static/scripts/gtag/gtag-stub.js` gepflegt —
 * der Stub läuft vor dem Bundle und kann nichts importieren. `consent.test.js`
 * liest die Stub-Datei und bricht, wenn die beiden auseinanderlaufen.
 */
export const CONSENT_STORAGE_KEY = 'cw24_consent_v1';

/** Erlaubte Entscheidungen. */
export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';

/**
 * Länder, in denen für nicht notwendige Cookies eine Einwilligung nötig ist:
 * EWR (EU-27 + Island, Liechtenstein, Norwegen) und das Vereinigte Königreich.
 * Wird als `region` an den Consent Mode übergeben.
 */
export const CONSENT_REQUIRED_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
];

/**
 * IANA-Zeitzonen der oben genannten Länder — inklusive der gängigen Aliase, weil
 * Browser je nach tzdb-Version mal die kanonische, mal die Landes-Zone melden
 * (z. B. `Europe/Bratislava` vs. `Europe/Prague`).
 */
const CONSENT_REQUIRED_TIME_ZONES = new Set([
  // EU-27
  'Europe/Vienna',
  'Europe/Brussels',
  'Europe/Sofia',
  'Europe/Zagreb',
  'Asia/Nicosia',
  'Asia/Famagusta',
  'Europe/Nicosia',
  'Europe/Prague',
  'Europe/Copenhagen',
  'Europe/Tallinn',
  'Europe/Helsinki',
  'Europe/Mariehamn',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Busingen',
  'Europe/Athens',
  'Europe/Budapest',
  'Europe/Dublin',
  'Europe/Rome',
  'Europe/Riga',
  'Europe/Vilnius',
  'Europe/Luxembourg',
  'Europe/Malta',
  'Europe/Amsterdam',
  'Europe/Warsaw',
  'Europe/Lisbon',
  'Atlantic/Azores',
  'Atlantic/Madeira',
  'Europe/Bucharest',
  'Europe/Bratislava',
  'Europe/Ljubljana',
  'Europe/Madrid',
  'Africa/Ceuta',
  'Atlantic/Canary',
  'Europe/Stockholm',
  // EWR ohne EU
  'Atlantic/Reykjavik',
  'Europe/Vaduz',
  'Europe/Oslo',
  'Arctic/Longyearbyen',
  // Vereinigtes Königreich (inkl. Kronbesitzungen mit gleichwertigen Regeln)
  'Europe/London',
  'Europe/Belfast',
  'Europe/Guernsey',
  'Europe/Isle_of_Man',
  'Europe/Jersey',
]);

/** Event, mit dem sich das Banner erneut öffnen lässt (Fusszeilen-Link). */
export const CONSENT_REOPEN_EVENT = 'cowork24:reopen-consent';

/**
 * Event nach einer Entscheidung. Google braucht es nicht (der Consent Mode
 * regelt das intern), aber Drittanbieter ohne Consent Mode — derzeit das
 * Reddit-Pixel — dürfen erst nach einer Zustimmung überhaupt geladen werden.
 * `util/DeferredAnalyticsScripts.js` hört darauf und bindet sie dann nach.
 */
export const CONSENT_CHANGED_EVENT = 'cowork24:consent-changed';

/**
 * Ob für die aufrufende Person eine Einwilligung eingeholt werden muss.
 * Nur im Browser aufrufen — im SSR gibt es keine Zeitzone der Nutzer:innen.
 *
 * @returns {boolean} true, wenn die Zeitzone auf EWR/UK hindeutet
 */
export const isConsentRequiredRegion = () => {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return false;
  }
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return CONSENT_REQUIRED_TIME_ZONES.has(timeZone);
  } catch (e) {
    // Kein Grund, den Seitenaufbau an einer Zeitzone scheitern zu lassen.
    return false;
  }
};

/**
 * Gespeicherte Entscheidung lesen.
 *
 * @returns {'granted'|'denied'|null} null, wenn noch nichts entschieden wurde
 */
export const readConsent = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored === CONSENT_GRANTED || stored === CONSENT_DENIED ? stored : null;
  } catch (e) {
    // Privater Modus / blockierter Speicher: dann eben jedes Mal fragen.
    return null;
  }
};

/**
 * Entscheidung speichern und an Google melden.
 *
 * @param {'granted'|'denied'} decision
 * @returns {void}
 */
export const saveConsent = decision => {
  if (decision !== CONSENT_GRANTED && decision !== CONSENT_DENIED) {
    return;
  }
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, decision);
  } catch (e) {
    // Nicht speicherbar — die Aktualisierung unten gilt trotzdem für diese Sitzung.
  }
  applyConsent(decision);
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: decision }));
};

/**
 * Consent-Mode-Signal senden. Ohne geladenes gtag.js landet der Aufruf im
 * dataLayer-Puffer des Stubs und wird beim Laden nachgeholt.
 *
 * @param {'granted'|'denied'} decision
 * @returns {void}
 */
export const applyConsent = decision => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  const value = decision === CONSENT_GRANTED ? CONSENT_GRANTED : CONSENT_DENIED;
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};

/**
 * Banner erneut öffnen (Widerruf muss so einfach sein wie die Einwilligung,
 * Art. 7 Abs. 3 DSGVO). Wird vom Fusszeilen-Link ausgelöst.
 *
 * @returns {void}
 */
export const reopenConsentBanner = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
};
